import { createRouter, createWebHistory } from "vue-router";

// Importing the views
import HomeView from "../views/HomeView.vue";
import StatDetailView from "../views/StatDetailView.vue";
import LearnView from "../views/LearnView.vue";
import LoginView from "../views/LoginView.vue";
import OnboardingView from "../views/OnboardingView.vue";
import OnboardingTest from "../views/OnboardingTest.vue";
import AccountSettings from "../views/AccountSettings.vue";
import VerifyEmail from "../views/VerifyEmail.vue";
import AuthCallback from "../views/AuthCallback.vue";
import LandingView from "../views/LandingView.vue";
import SubscriptionsView from "../views/SubscriptionsView.vue";
import SubscriptionsMarketingView from "../views/SubscriptionsMarketingView.vue";
import WhitePaperView from "../views/WhitePaperView.vue";
import TermsPrivacyView from "../views/TermsPrivacyView.vue";

import ResetPassword from "../views/ResetPassword.vue";

import { supabase } from "../lib/supabase.js";
import { useUserStore } from "../stores/user.js";
import { usePostHog } from "../composables/usePostHog.js";

// Setting up the router
const isLocalHost =
  typeof window !== "undefined" &&
  (window.location.hostname === "localhost" ||
    window.location.hostname === "127.0.0.1" ||
    window.location.hostname === "::1");

const host = typeof window !== "undefined" ? window.location.hostname : "";
const isLearnHost =
  isLocalHost || (typeof host === "string" && host.startsWith("learn."));
const isMarketingHost = !isLearnHost;

function getApexDomain(h) {
  const parts = (h || "").split(".");
  return parts.slice(-2).join(".");
}

const routes = [
  { path: "/", name: "Landing", component: LandingView },
  { path: "/landing", name: "Landing (Local)", component: LandingView },
  {
    path: "/subscriptions",
    name: "Subscriptions",
    component: isMarketingHost ? SubscriptionsMarketingView : SubscriptionsView,
  },
  { path: "/white-paper", name: "White Paper", component: WhitePaperView },
  { path: "/terms", name: "Terms and Privacy", component: TermsPrivacyView },
  { path: "/home", name: "Home", component: HomeView },
  {
    path: "/stats/:stat",
    name: "Stat Detail",
    component: StatDetailView,
  },
  { path: "/learn", name: "Learn", component: LearnView },
  { path: "/login", name: "Login", component: LoginView },
  { path: "/onboarding", name: "Onboarding", component: OnboardingView },
  {
    path: "/onboarding-test",
    name: "Onboarding Test",
    component: OnboardingTest,
  },
  { path: "/account", name: "Account Settings", component: AccountSettings },
  { path: "/verify-email", name: "Verify Email", component: VerifyEmail },
  { path: "/auth/callback", name: "Auth Callback", component: AuthCallback },
  { path: "/reset-password", name: "Reset Password", component: ResetPassword },
];

// Register secret local-only route (dev-only to avoid bundling in prod)
if (process.env.NODE_ENV === "development") {
  if (isLocalHost) {
    routes.push({
      path: "/__local/metrics",
      name: "Local Metrics",
      component: () =>
        import(
          /* webpackChunkName: "local-metrics" */ "../views/LocalMetrics.vue"
        ),
    });
    routes.push({
      path: "/__local/card-review",
      name: "Local Card Review",
      component: () =>
        import(
          /* webpackChunkName: "local-card-review" */ "../views/LocalCardReview.vue"
        ),
    });
  }
}

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition;
    }
    if (to.hash) {
      return { el: to.hash, top: 0 };
    }
    return { left: 0, top: 0 };
  },
});

router.beforeEach(async (to) => {
  // Redirect Supabase auth responses (code in query or tokens in hash)
  const hasCode = typeof to.query.code === "string";
  const hash = to.hash || "";
  const hasTokensInHash =
    hash.includes("access_token=") ||
    hash.includes("refresh_token=") ||
    hash.includes("error=");
  if (
    to.path !== "/auth/callback" &&
    to.path !== "/reset-password" &&
    (hasCode || hasTokensInHash)
  ) {
    return { path: "/auth/callback", query: to.query, hash };
  }
  // Domain-aware routing: marketing (lexical.app) vs learn (learn.lexical.app)
  if (isMarketingHost) {
    // Marketing host: only landing page is handled here; app routes redirect to learn subdomain
    if (
      to.path === "/" ||
      to.path === "/subscriptions" ||
      to.path === "/white-paper" ||
      to.path === "/terms"
    ) {
      return true;
    }
    const target =
      `${window.location.protocol}//learn.${getApexDomain(
        typeof window !== "undefined" ? window.location.hostname : ""
      )}` + (to.fullPath || to.path);
    window.location.href = target;
    return false;
  }
  // Learn host or local dev: ensure "/" maps to the app home
  if (isLearnHost && to.path === "/") {
    return { path: "/home" };
  }

  // Allow secret local routes in local dev to bypass auth/onboarding
  if (
    process.env.NODE_ENV === "development" &&
    isLocalHost &&
    (to.path === "/__local/metrics" || to.path === "/__local/card-review")
  ) {
    return true;
  }

  // Centralized auth and onboarding redirects
  const publicPaths = new Set([
    "/login",
    "/verify-email",
    "/auth/callback",
    "/reset-password",
    "/landing",
    "/subscriptions",
    "/white-paper",
    "/terms",
  ]);
  // Get current session
  const {
    data: { session },
  } = await supabase.auth.getSession();

  if (!session || !session.user) {
    // Not authenticated: only allow public routes
    if (!publicPaths.has(to.path)) {
      return { path: "/login", query: { redirect: to.fullPath } };
    }
    return true;
  }

  // Authenticated: fetch user to read metadata (language)
  const { data: userData } = await supabase.auth.getUser();
  const user = userData?.user ?? null;

  // Best-effort: hydrate store if available (prevents page-level checks from misfiring)
  try {
    const userStore = useUserStore();
    userStore.user = user;
    userStore.isLoggedIn = true;
  } catch (_e) {
    // If Pinia is not yet available, skip without failing navigation
  }

  // Read active language and onboarding test status from user_preferences
  let language = null;
  let pref = null;
  try {
    const { data: prefData, error: prefErr } = await supabase
      .from("user_preferences")
      .select("active_language, starting_word_rank")
      .eq("user_id", user?.id)
      .maybeSingle();
    if (!prefErr) {
      pref = prefData || null;
      language = pref?.active_language ?? null;
    } else {
      console.warn("Router: failed to load user_preferences", {
        code: prefErr.code,
        message: prefErr.message,
        details: prefErr.details,
      });
    }
  } catch (_e) {
    // Leave language as null on error
  }

  // Gate LearnView with onboarding test completion (only replace Learn with test)
  let needsTest = false;
  if (language) {
    const sr = pref?.starting_word_rank?.[language];
    needsTest = !(typeof sr === "number");
  }
  // Only redirect when navigating to Learn; allow Home and Account Settings during test
  if (needsTest && to.path === "/learn") {
    return { path: "/onboarding-test" };
  }
  // If test is complete, keep users away from /onboarding-test
  if (!needsTest && to.path === "/onboarding-test") {
    return { path: "/learn" };
  }

  // If logged in but no language, force onboarding except for auth callback
  if (!language && to.path !== "/onboarding" && to.path !== "/auth/callback") {
    return { path: "/onboarding" };
  }

  // If language is set, keep them away from login/onboarding
  if (language && (to.path === "/login" || to.path === "/onboarding")) {
    return { path: "/home" };
  }

  return true;
});

// eslint-disable-next-line no-unused-vars
const { posthog } = usePostHog();

export default router;
